import React, { useEffect, useState } from "react";
import { useLoading, useUser } from "@/contexts";
import {
  plansService,
  PaymentHistoryItem,
  PaymentHistoryEnum,
} from "@/services";
import { formatDate, formatPrice } from "@/utils";
import { Pagination, PremiumAccessModal } from "@/components";
import "./Payments.css";

const PaymentsScreen: React.FC = () => {
  const { userData, user: currentUser, excludedUserIds } = useUser();
  const { withLoading } = useLoading();

  const isFemale = userData?.gender === "Feminino";
  const isFemaleIsVerified = userData?.isVerified;

  const isShowPremiumAccessModal = !!(isFemale && !isFemaleIsVerified);

  const [history, setHistory] = useState<PaymentHistoryItem[]>([]);
  const [lastId, setLastId] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [showPremiumAccessModal, setShowPremiumAccessModal] = useState(
    isShowPremiumAccessModal,
  );

  const fetchHistory = async (isLoadMore = false) => {
    const action = async () => {
      if (isLoadMore) setLoadingMore(true);

      const result = await plansService.getUserPlansHistory(
        5,
        isLoadMore ? lastId : undefined,
      );

      if (isLoadMore) {
        setHistory((prev) => [...prev, ...result.history]);
      } else {
        setHistory(result.history);
      }

      setLastId(result.lastVisibleId || undefined);
      setHasMore(result.hasMore);
      setLoadingMore(false);
    };

    // Usa o loading global apenas no primeiro fetch, no "ver mais" usa o local
    if (isLoadMore) await action();
    else await withLoading(action);
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const getStatusInfo = (status: string) => {
    switch (status) {
      case PaymentHistoryEnum.SUCCEEDED:
        return { label: "Pago", class: "status-succeeded" };
      case PaymentHistoryEnum.REQUIRES_ACTION:
        return { label: "Pendente", class: "status-pending" };
      // case PaymentHistoryEnum.PENDING:
      //   return { label: "Pendente", class: "status-pending" };
      default:
        return { label: "Falhou", class: "status-failed" };
    }
  };

  return (
    <div className="container payment-page">
      <header className="page-header">
        <h1 className="auth-title">Meus Pagamentos</h1>
        <p className="auth-subtitle">Histórico de assinaturas e faturas</p>
      </header>

      <div className="payments-list">
        {history.length > 0 ? (
          <>
            {history.map((item) => {
              const status = getStatusInfo(item.status);
              const date = formatDate(item.createdAt._seconds);

              return (
                <div key={item.id} className="approval-card payment-item">
                  <div className="payment-main-info">
                    <div className="payment-details">
                      <span className="payment-date">{date}</span>
                      <strong className="payment-plan-name">
                        Plano: {item.planId}
                      </strong>
                      <span className="payment-method">
                        {item.paymentMethodType?.toUpperCase()}
                      </span>

                      {item.paymentMethodType === "boleto" &&
                        item.boletoUrl && (
                          <div className="payment-actions">
                            <a
                              href={item.boletoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-view-boleto"
                            >
                              <span className="icon">📄</span> Visualizar Boleto
                            </a>
                          </div>
                        )}
                    </div>
                    <div className="payment-value-area">
                      <span className="payment-amount">
                        {formatPrice(item.amount)}
                      </span>
                      <span className={`status-badge ${status.class}`}>
                        {status.label}
                      </span>
                    </div>
                  </div>

                  {item.status === "pending" && (
                    <div className="approval-message">
                      <p>
                        Aguardando compensação do pagamento. Se for boleto, pode
                        levar até 3 dias úteis.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}

            <Pagination
              hasMore={hasMore}
              loading={loadingMore}
              onLoadMore={() => fetchHistory(true)}
            />
          </>
        ) : (
          <div className="approval-card center" style={{ textAlign: "center" }}>
            <div className="approval-icon">💳</div>
            <p style={{ color: "#fff" }}>
              Você ainda não possui histórico de pagamentos.
            </p>
          </div>
        )}
      </div>

      <PremiumAccessModal
        isOpen={showPremiumAccessModal}
        onClose={() => setShowPremiumAccessModal(false)}
      />
    </div>
  );
};

export default PaymentsScreen;
